/**
 * Hero social-icons lift.
 *
 * The Instagram / TikTok / Pinterest icons live inside the hero's copy wrapper,
 * but design requires them pinned to the top of the hero — level with the
 * header action icons and flush-left with the headline. CSS alone can't do this
 * clickably: `.hero` establishes a stacking context (z-index:1) that sits below
 * the header (z-index:16), so any descendant — even position:fixed — paints
 * (and receives clicks) below the header.
 *
 * Fix: move the social-icons node OUT of `.hero` to be a direct child of
 * `.hero-wrapper` (which creates no stacking context), where a z-index above the
 * header wins at the root level. Positioning is handled in CSS
 * (aurora-ambiance.css), scoped to `.hero-wrapper > .social-icons__wrapper`.
 *
 * Progressive enhancement: without JS the icons still render in the copy flow —
 * just not lifted. Only wide desktop layouts lift them; tablets and compact
 * laptops keep the icons with the Hero copy so they cannot collide with the
 * responsive header controls.
 */

(() => {
  const DESKTOP = '(min-width: 1200px)';
  const desktopMedia = window.matchMedia(DESKTOP);

  function syncSocialPosition() {
    const heroWrapper = document.querySelector('.hero-wrapper');
    if (!heroWrapper) return;

    const content = heroWrapper.querySelector('.hero__content-wrapper');
    const social = heroWrapper.querySelector('.social-icons__wrapper');
    if (!content || !social) return;

    if (!desktopMedia.matches) {
      // The viewport can change without a page reload (responsive preview,
      // device rotation, or a resized browser). Return the icons to the first
      // position in the mobile content flow so all adjacent-block selectors
      // and the intended editorial order remain valid.
      if (social.parentElement !== content) {
        content.insertBefore(social, content.firstElementChild);
      }

      social.style.removeProperty('will-change');
      social.removeAttribute('data-hero-social-lifted');
      return;
    }

    if (!social) return;

    // Already lifted? (idempotent across re-inits / section reloads)
    if (social.parentElement === heroWrapper) return;

    // Cancel the reveal-stagger will-change that would otherwise establish a
    // containing block, then relocate out of `.hero`.
    social.style.willChange = 'auto';
    heroWrapper.appendChild(social);
    social.setAttribute('data-hero-social-lifted', '');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncSocialPosition, { once: true });
  } else {
    syncSocialPosition();
  }

  // Keep the DOM structure in sync when responsive preview changes size,
  // phones rotate, or the theme editor re-renders the hero section.
  if (typeof desktopMedia.addEventListener === 'function') {
    desktopMedia.addEventListener('change', syncSocialPosition);
  } else {
    desktopMedia.addListener(syncSocialPosition);
  }

  document.addEventListener('shopify:section:load', syncSocialPosition);
})();
